import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/services/api';
import { IssuePeekOverview } from '@/components/IssuePeekOverview';
import {
  Plus,
  Boxes,
  ChevronRight,
  Trash2,
  Edit2,
  CheckCircle2,
  Circle,
  Target,
  User,
} from 'lucide-react';
import clsx from 'clsx';

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  completed: { label: 'Completed', color: 'text-green-700', bg: 'bg-green-100' },
  in_progress: { label: 'In Progress', color: 'text-blue-700', bg: 'bg-blue-100' },
  planned: { label: 'Planned', color: 'text-gray-700', bg: 'bg-gray-100' },
  cancelled: { label: 'Cancelled', color: 'text-red-700', bg: 'bg-red-100' },
};

export function Modules() {
  const { workspaceId, projectId } = useParams();
  const api = useApi();
  const queryClient = useQueryClient();

  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingModule, setEditingModule] = useState<string | null>(null);
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'planned',
    lead: '',
    startDate: '',
    targetDate: '',
  });

  const { data: modules, isLoading } = useQuery({
    queryKey: ['modules', projectId],
    queryFn: () => api.getModules(projectId!),
    enabled: !!projectId,
  });

  const { data: workItems } = useQuery({
    queryKey: ['workitems', projectId],
    queryFn: () => api.getWorkItems(projectId!),
    enabled: !!projectId,
  });

  const { data: states } = useQuery({
    queryKey: ['states', projectId],
    queryFn: () => api.getStates(projectId!),
    enabled: !!projectId,
  });

  const { data: members } = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: () => api.getMembers(workspaceId!),
    enabled: !!workspaceId,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => api.createModule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modules', projectId] });
      setIsCreating(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.updateModule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modules', projectId] });
      setEditingModule(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteModule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modules', projectId] });
      queryClient.invalidateQueries({ queryKey: ['workitems', projectId] });
      if (selectedModuleId === editingModule) setSelectedModuleId(null);
    },
  });

  const resetForm = () => {
    setFormData({ name: '', description: '', status: 'planned', lead: '', startDate: '', targetDate: '' });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    if (editingModule) {
      updateMutation.mutate({
        id: editingModule,
        data: {
          bh_name: formData.name,
          bh_description: formData.description,
          bh_status: formData.status,
          bh_lead: formData.lead || null,
          bh_startdate: formData.startDate || null,
          bh_targetdate: formData.targetDate || null,
        },
      });
    } else {
      createMutation.mutate({
        bh_name: formData.name,
        bh_description: formData.description,
        bh_status: formData.status,
        bh_lead: formData.lead || null,
        bh_startdate: formData.startDate || null,
        bh_targetdate: formData.targetDate || null,
        bh_project: projectId,
      });
    }
  };

  const startEditing = (module: any) => {
    setEditingModule(module.bh_moduleid);
    setFormData({
      name: module.bh_name,
      description: module.bh_description || '',
      status: module.bh_status || 'planned',
      lead: module.bh_lead || '',
      startDate: module.bh_startdate || '',
      targetDate: module.bh_targetdate || '',
    });
    setIsCreating(true);
  };

  const getModuleWorkItems = (moduleId: string) => {
    return workItems?.filter((wi: any) => wi.bh_module === moduleId) || [];
  };

  const getModuleProgress = (moduleId: string) => {
    const items = getModuleWorkItems(moduleId);
    if (items.length === 0) return { total: 0, completed: 0, percentage: 0 };

    const completed = items.filter((wi: any) => {
      const state = states?.find((s: any) => s.bh_stateid === wi._bh_state_value);
      return state?.bh_group === 4;
    }).length;

    return {
      total: items.length,
      completed,
      percentage: Math.round((completed / items.length) * 100),
    };
  };

  const getStateById = (stateId: string) => states?.find((s: any) => s.bh_stateid === stateId);
  const getMemberById = (memberId: string) => members?.find((m: any) => m.bh_memberid === memberId);

  const formatDateRange = (start: string, end: string) => {
    if (!start && !end) return 'No dates set';
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    const startStr = start ? new Date(start).toLocaleDateString('en-US', options) : '?';
    const endStr = end ? new Date(end).toLocaleDateString('en-US', options) : '?';
    return `${startStr} - ${endStr}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const selectedModule = modules?.find((m: any) => m.bh_moduleid === selectedModuleId);

  return (
    <div className="h-full flex">
      {/* Modules List */}
      <div className="w-80 border-r border-gray-200 bg-white flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-lg font-semibold text-gray-900">Modules</h1>
            <button
              onClick={() => {
                setIsCreating(true);
                setEditingModule(null);
                resetForm();
              }}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>

          {isCreating && (
            <form onSubmit={handleSubmit} className="space-y-3 p-3 bg-gray-50 rounded-lg">
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Module name"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                autoFocus
              />
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description (optional)"
                rows={2}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
              <div>
                <label className="block text-xs text-gray-500 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="planned">Planned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Lead</label>
                <select
                  value={formData.lead}
                  onChange={(e) => setFormData({ ...formData, lead: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">No lead</option>
                  {members?.map((member: any) => (
                    <option key={member.bh_memberid} value={member.bh_memberid}>
                      {member.bh_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Start</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Target</label>
                  <input
                    type="date"
                    value={formData.targetDate}
                    onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={!formData.name.trim()}
                  className="flex-1 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {editingModule ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsCreating(false);
                    setEditingModule(null);
                    resetForm();
                  }}
                  className="px-3 py-2 text-gray-600 text-sm hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {modules?.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Boxes className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No modules yet</p>
              <p className="text-sm">Group related features together</p>
            </div>
          ) : (
            <div className="space-y-2">
              {modules?.map((module: any) => {
                const progress = getModuleProgress(module.bh_moduleid);
                const status = STATUS_CONFIG[module.bh_status] || STATUS_CONFIG.planned;
                const lead = getMemberById(module.bh_lead);

                return (
                  <button
                    key={module.bh_moduleid}
                    onClick={() => setSelectedModuleId(module.bh_moduleid)}
                    className={clsx(
                      'w-full p-3 rounded-lg text-left transition-colors',
                      selectedModuleId === module.bh_moduleid
                        ? 'bg-primary-50 border-2 border-primary-500'
                        : 'hover:bg-gray-50 border-2 border-transparent'
                    )}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{module.bh_name}</h3>
                        <p className="text-xs text-gray-500">
                          {formatDateRange(module.bh_startdate, module.bh_targetdate)}
                        </p>
                      </div>
                      <span className={clsx('px-2 py-0.5 text-xs font-medium rounded-full', status.bg, status.color)}>
                        {status.label}
                      </span>
                    </div>

                    {lead && (
                      <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                        <User className="w-3 h-3" />
                        {lead.bh_name}
                      </div>
                    )}

                    {/* Progress bar */}
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span>{progress.completed}/{progress.total} issues</span>
                        <span>{progress.percentage}%</span>
                      </div>
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 rounded-full transition-all"
                          style={{ width: `${progress.percentage}%` }}
                        />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Module Detail */}
      <div className="flex-1 bg-gray-50 overflow-y-auto">
        {selectedModule ? (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{selectedModule.bh_name}</h2>
                {selectedModule.bh_description && (
                  <p className="text-gray-600 mt-1">{selectedModule.bh_description}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => startEditing(selectedModule)}
                  className="p-2 hover:bg-gray-200 rounded-lg text-gray-600"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    if (confirm('Delete this module?')) {
                      deleteMutation.mutate(selectedModule.bh_moduleid);
                    }
                  }}
                  className="p-2 hover:bg-red-100 rounded-lg text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Module Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              {(() => {
                const progress = getModuleProgress(selectedModule.bh_moduleid);
                const items = getModuleWorkItems(selectedModule.bh_moduleid);
                const inProgress = items.filter((wi: any) => {
                  const state = states?.find((s: any) => s.bh_stateid === wi._bh_state_value);
                  return state?.bh_group === 3;
                }).length;
                const lead = getMemberById(selectedModule.bh_lead);

                return (
                  <>
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                        <Target className="w-4 h-4" />
                        Total Issues
                      </div>
                      <p className="text-2xl font-semibold text-gray-900">{progress.total}</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 text-blue-500 text-sm mb-1">
                        <Circle className="w-4 h-4" />
                        In Progress
                      </div>
                      <p className="text-2xl font-semibold text-gray-900">{inProgress}</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 text-green-500 text-sm mb-1">
                        <CheckCircle2 className="w-4 h-4" />
                        Completed
                      </div>
                      <p className="text-2xl font-semibold text-gray-900">{progress.completed}</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                        <User className="w-4 h-4" />
                        Lead
                      </div>
                      <p className="text-lg font-semibold text-gray-900 truncate">
                        {lead?.bh_name || 'Unassigned'}
                      </p>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Issues in Module */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="font-medium text-gray-900">Issues in this module</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {getModuleWorkItems(selectedModule.bh_moduleid).length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    No issues assigned to this module
                  </div>
                ) : (
                  getModuleWorkItems(selectedModule.bh_moduleid).map((item: any) => {
                    const state = getStateById(item._bh_state_value);
                    return (
                      <button
                        key={item.bh_workitemid}
                        onClick={() => setSelectedIssueId(item.bh_workitemid)}
                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 text-left"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: state?.bh_color || '#6b7280' }}
                        />
                        <span className="text-sm text-gray-500 font-mono">
                          #{item.bh_sequenceid}
                        </span>
                        <span className="flex-1 text-sm text-gray-900 truncate">
                          {item.bh_name}
                        </span>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <Boxes className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">Select a module to view details</p>
            </div>
          </div>
        )}
      </div>

      {/* Issue Peek Overview */}
      {selectedIssueId && projectId && (
        <IssuePeekOverview
          issueId={selectedIssueId}
          projectId={projectId}
          onClose={() => setSelectedIssueId(null)}
        />
      )}
    </div>
  );
}
